import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Loader2, Sparkles, X } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional time to pre-fill (clicking an empty slot on a week view). */
  defaultStart?: Date;
  /** Optional duration in minutes for the pre-filled slot. Default 30. */
  defaultDurationMin?: number;
}

const DEFAULT_DURATION_MIN = 30;

/**
 * Form-based quick-add. NL parsing is layered on top in Phase D — this component
 * stays usable as the always-available form fallback.
 */
export function QuickAddDialog({
  open,
  onOpenChange,
  defaultStart,
  defaultDurationMin = DEFAULT_DURATION_MIN,
}: Props) {
  const queryClient = useQueryClient();
  const statusQuery = useQuery({
    queryKey: qk.calendarStatus,
    queryFn: () => api.listCalendarAccountStatuses(),
    enabled: open,
  });
  const connected = (statusQuery.data?.accounts ?? []).filter((a) => a.hasCalendarScope);

  const [accountId, setAccountId] = useState<string>('');
  const [summary, setSummary] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [attendeesInput, setAttendeesInput] = useState('');
  const [naturalInput, setNaturalInput] = useState('');
  const [nlBusy, setNlBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const start = defaultStart ?? roundUpToQuarterHour(new Date());
    const end = new Date(start.getTime() + defaultDurationMin * 60_000);
    setDate(format(start, 'yyyy-MM-dd'));
    setStartTime(format(start, 'HH:mm'));
    setEndTime(format(end, 'HH:mm'));
    setSummary('');
    setLocation('');
    setDescription('');
    setAttendeesInput('');
    setNaturalInput('');
    setIsAllDay(false);
  }, [open, defaultStart, defaultDurationMin]);

  useEffect(() => {
    if (open && connected.length > 0 && !accountId) {
      setAccountId(connected[0]!.accountId);
    }
  }, [open, connected, accountId]);

  const createMutation = useMutation({
    mutationFn: () => {
      const startIso = composeIso(date, startTime, isAllDay);
      const endIso = composeIso(date, endTime, isAllDay);
      const attendees = parseAttendees(attendeesInput);
      return api.createCalendarEvent({
        accountId,
        summary: summary.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        startIso,
        endIso,
        isAllDay,
        attendees: attendees.length > 0 ? attendees : undefined,
      });
    },
    onSuccess: () => {
      toast.success('Event created.');
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      onOpenChange(false);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'Could not create event.';
      toast.error(message);
    },
  });

  async function handleNaturalParse() {
    if (!naturalInput.trim()) return;
    setNlBusy(true);
    try {
      const result = await api.parseEventText(naturalInput.trim(), accountId || undefined);
      const parsed = result.parsed;
      const startD = new Date(parsed.startIso);
      const endD = new Date(parsed.endIso);
      setSummary(parsed.summary);
      setDate(format(startD, 'yyyy-MM-dd'));
      setStartTime(format(startD, 'HH:mm'));
      setEndTime(format(endD, 'HH:mm'));
      setIsAllDay(parsed.isAllDay);
      if (parsed.location) setLocation(parsed.location);
      if (parsed.description) setDescription(parsed.description);
      if (parsed.attendees && parsed.attendees.length > 0) {
        setAttendeesInput(parsed.attendees.join(', '));
      }
      if (parsed.confidence < 0.5) {
        toast.info(`Low confidence (${Math.round(parsed.confidence * 100)}%) — please review.`);
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Could not parse.';
      toast.error(message);
    } finally {
      setNlBusy(false);
    }
  }

  const canSubmit =
    accountId && summary.trim().length > 0 && date && (isAllDay || (startTime && endTime));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New event</DialogTitle>
          <DialogDescription>
            Type naturally or fill the form. Attendees will be emailed an invite.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
              <Sparkles className="h-3 w-3" /> Quick add
            </div>
            <div className="flex gap-2">
              <Input
                value={naturalInput}
                onChange={(e) => setNaturalInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleNaturalParse();
                  }
                }}
                placeholder="Lunch with Stuart Tue 1pm at Bills"
                disabled={nlBusy}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleNaturalParse()}
                disabled={nlBusy || !naturalInput.trim()}
              >
                {nlBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Parse'}
              </Button>
            </div>
          </div>

          <div>
            <Label>Account</Label>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm"
            >
              {connected.length === 0 && <option value="">No calendar-connected accounts</option>}
              {connected.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label>Title</Label>
            <Input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What's the event?"
            />
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              id="isAllDay"
              type="checkbox"
              checked={isAllDay}
              onChange={(e) => setIsAllDay(e.target.checked)}
            />
            <label htmlFor="isAllDay">All day</label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            {!isAllDay && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Start</Label>
                  <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                </div>
                <div>
                  <Label>End</Label>
                  <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Location</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>

          <div>
            <Label>Attendees (comma-separated emails)</Label>
            <Input
              value={attendeesInput}
              onChange={(e) => setAttendeesInput(e.target.value)}
              placeholder="stuart@example.com, jess@example.com"
            />
          </div>

          <div>
            <Label>Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            disabled={!canSubmit || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Creating...
              </>
            ) : (
              'Create event'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function roundUpToQuarterHour(d: Date): Date {
  const minutes = d.getMinutes();
  const rounded = Math.ceil(minutes / 15) * 15;
  const next = new Date(d);
  next.setMinutes(rounded);
  next.setSeconds(0);
  next.setMilliseconds(0);
  return next;
}

function composeIso(dateStr: string, timeStr: string, allDay: boolean): string {
  if (allDay) {
    // For all-day, ISO date at midnight in local zone (server converts to date-only).
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toISOString();
  }
  const d = new Date(`${dateStr}T${timeStr}:00`);
  return d.toISOString();
}

function parseAttendees(raw: string): string[] {
  return raw
    .split(/[,\s;]+/)
    .map((s) => s.trim())
    .filter((s) => /.+@.+\..+/.test(s));
}
